import { FullscreenToggle } from './fullscreen-toggle.js';

/**
 * fullscreen-gesture.js
 *
 * The second door into full screen: press twice, quickly, anywhere the screen
 * is not already listening. It exists because the trigger in the top-right
 * corner is small, deliberately faded during a match, and easy never to notice
 * — while "double-click to go full screen" is a reflex most players already
 * carry in from video.
 *
 * A DOOR IN, NOT A DOOR OUT. It used to toggle, and the costly accident was
 * always leaving: a player in full screen chose it, and one stray pair on the
 * felt mid-match undid that choice and reflowed the board under their cards.
 * Entering by accident costs a bigger screen and an Esc the browser itself
 * announces on every entry. So the gesture only ever enters; Esc, Android's
 * Back and the control in the chrome menu are the ways out.
 *
 * ANYWHERE IS THE HARD WORD. This game's primary object is a card — pressed
 * to read it (tap to inspect) and dragged to play it — and its lobby is
 * wall-to-wall glass buttons, so a gesture that fired on literally anything
 * would put the page into full screen every time someone read two cards in a
 * row. What it fires on is INERT surface: the felt, a backdrop, the empty half
 * of a panel, anything that does not itself react to a press. The test for
 * that is the computed cursor plus the roles and tags a browser already treats
 * as controls, NOT a list of this game's class names — no such list survives
 * the next screen someone adds. A pressable cursor is the hand of a control
 * (`pointer`), of a thing you pick up (`grab`, `grabbing`, `move` — every battle
 * card since dragging became the only way to play one), or of a control that
 * is busy (`progress`).
 *
 * TWO DOORS, ONE WAY IN. The pointer pair below is what a phone produces, and
 * it carries a fixed 400ms window; the browser's own `dblclick` knows the
 * player's system double-click speed, which we do not. Both are wired; the
 * pair consumes the tap record so the dblclick behind it finds nothing, and
 * since neither door can leave, a repeat request is a harmless no-op rather
 * than an enter-and-leave in the same breath.
 *
 * It is capability-gated exactly like the control it drives: on an iPhone
 * there is no element-level Fullscreen API at all, so nothing is wired and the
 * gesture is silent rather than a press that appears to do nothing.
 * Contract: tests/unit/ui/fullscreenGesture.test.js.
 */

let FullscreenGesture = {};

FullscreenGesture = (function () {
    /* The pair. 400ms is the long end of a comfortable double-click and the
       short end of two separate intentions; 32px allows for a finger landing
       twice on a phone without allowing two presses aimed at different things. */
    const PAIR_MS = 400;
    const PAIR_SLOP = 32;

    /* Each half. A press that is held or that travels is a hold or a drag —
       this is what keeps a card drag, a slider, and a long-press inspect from
       ever counting as one tap of a pair. */
    const PRESS_MS = 500;
    const TAP_SLOP = 12;

    const PULSE_CLASS = 'fullscreen-toggle-pulsing';
    const PULSE_MS = 900;

    /**
     * What a browser already treats as a control, by tag, by role, or by being
     * reachable from the keyboard. The computed-cursor test below catches this
     * game's own pressable surfaces; this catches the ones that are pressable
     * without saying so in CSS.
     */
    const INTERACTIVE = [
        'a[href]', 'area[href]', 'button', 'input', 'select', 'textarea',
        'label', 'summary', 'details', 'option', 'optgroup',
        'video', 'audio', 'iframe', 'embed', 'object',
        '[contenteditable]:not([contenteditable="false"])',
        '[tabindex]:not([tabindex="-1"])',
        '[role="button"]', '[role="link"]', '[role="checkbox"]', '[role="radio"]',
        '[role="switch"]', '[role="tab"]', '[role="menuitem"]',
        '[role="menuitemcheckbox"]', '[role="menuitemradio"]', '[role="option"]',
        '[role="slider"]', '[role="spinbutton"]', '[role="textbox"]',
        '[role="combobox"]', '[role="listbox"]', '[role="treeitem"]',
    ].join(',');

    /* How stale the tap record may be and still describe the double-click the
       browser just reported. Generous, because the whole point of the
       `dblclick` door is that the system's threshold can be slower than ours. */
    const RECORD_MS = 1500;

    /** @type {{id:number, x:number, y:number, at:number}|null} */
    let press = null;
    /** The two most recent taps, oldest first. Both doors read the same record. */
    /** @type {{target:EventTarget, x:number, y:number, at:number}|null} */
    let firstTap = null;
    /** @type {{target:EventTarget, x:number, y:number, at:number}|null} */
    let lastTap = null;
    let wired = false;

    const now = () => Date.now();
    const apart = (ax, ay, bx, by) => Math.hypot(ax - bx, ay - by);

    /**
     * THE GESTURE EXISTS EXACTLY WHERE THE BUTTON DOES.
     *
     * One question, asked of the module that already answers it: the trigger is
     * built only where the Fullscreen API is real and permitted and the page is
     * not an embedded frame, so its presence covers every one of those cases at
     * once and cannot drift away from them.
     */
    function available() {
        const el = FullscreenToggle.element?.();
        return !!(el && el.isConnected);
    }

    const PRESSABLE_CURSORS = new Set(['pointer', 'grab', 'grabbing', 'move', 'progress']);

    function cursorOf(el) {
        try {
            return window.getComputedStyle(el).cursor || '';
        } catch (_) {
            return '';
        }
    }

    /** True when nothing from here up to the document reacts to a press. */
    function isInert(node) {
        let el = node instanceof Element ? node : node?.parentElement || null;
        while (el && el !== document.documentElement) {
            if (typeof el.matches === 'function' && el.matches(INTERACTIVE)) return false;
            if (el.draggable === true) return false;
            if (PRESSABLE_CURSORS.has(cursorOf(el))) return false;
            el = el.parentElement;
        }
        return true;
    }

    /**
     * A double-click on prose selects the word under it. That press was aimed
     * at the text, so the screen must not move underneath it.
     */
    function selecting() {
        try {
            const selection = window.getSelection?.();
            return !!(selection && selection.isCollapsed === false
                && String(selection).trim() !== '');
        } catch (_) {
            return false;
        }
    }

    /**
     * A ring around the real trigger. A gesture that silently rearranges the
     * whole screen leaves the player unsure what they just did; the pulse names
     * the control responsible, which is also where they will look to undo it.
     */
    function pulse() {
        const el = FullscreenToggle.element?.();
        if (!el) return;
        const done = () => {
            el.classList.remove(PULSE_CLASS);
            el.removeEventListener('animationend', done);
        };
        done();
        // Reading layout between the two writes is what restarts an animation
        // that is already running, for a gesture repeated inside its own tail.
        void el.offsetWidth;
        el.classList.add(PULSE_CLASS);
        el.addEventListener('animationend', done);
        /* Reduced motion removes the animation, and with it the event that
           would have taken the class off again — so the timeout is the one that
           always fires, and it also detaches a listener that never will. A
           `{ once: true }` listener that no event reaches stays attached for the
           life of the page, one per gesture. */
        setTimeout(done, PULSE_MS);
    }

    /** Run the gesture, if this really was one. */
    function attempt(target) {
        if (!available() || selecting() || !isInert(target)) return;
        // Already in: nothing to do, and nothing for the ring to point at.
        if (FullscreenToggle.isFullscreen()) return;
        FullscreenToggle.enter();
        pulse();
    }

    function reset() {
        press = null;
        firstTap = null;
        lastTap = null;
    }

    function onDown(event) {
        if (event.isPrimary === false) { reset(); return; }
        press = { id: event.pointerId, x: event.clientX, y: event.clientY, at: now() };
    }

    function onUp(event) {
        if (event.isPrimary === false) { reset(); return; }
        const started = press;
        press = null;

        const x = event.clientX;
        const y = event.clientY;
        const isTap = !!started
            && started.id === event.pointerId
            && now() - started.at <= PRESS_MS
            && apart(started.x, started.y, x, y) <= TAP_SLOP;
        // A drag, a hold, or an up with no down of ours breaks any pair in
        // progress — it is a different intention, not the second half of one.
        if (!isTap) { firstTap = null; lastTap = null; return; }

        firstTap = lastTap;
        lastTap = { target: event.target, x, y, at: now() };
        if (!firstTap) return;

        const paired = firstTap.target === event.target
            && lastTap.at - firstTap.at <= PAIR_MS
            && apart(firstTap.x, firstTap.y, x, y) <= PAIR_SLOP;
        if (!paired) return;

        // Consumed either way: a third press starts a fresh pair rather than
        // toggling again on every press of a rapid burst.
        reset();
        attempt(event.target);
    }

    /**
     * THE BROWSER'S OWN DOUBLE-CLICK — a second door, not a second policy.
     *
     * It exists only to widen the window: the system knows the player's
     * double-click speed and we do not, so a pair slower than PAIR_MS still
     * counts if the browser calls it one. Everything else is read from the tap
     * record, NOT from this event.
     *
     * That matters more than it looks. `dblclick.target` is the nearest common
     * ancestor of the two clicks, so when the first press removes what it hit —
     * a button that swaps the screen, a backdrop that dismisses — the two
     * clicks land on different elements and this arrives on their PARENT, which
     * is usually inert. Measured in Chromium touch emulation: two taps on the
     * lobby's Tutorial button, which starts the tutorial on the first one, and
     * the synthesized double-click surfaced on an inert ancestor and toggled
     * full screen. Requiring both recorded taps to be the very element this
     * event names closes that, and costs nothing on a real double-click, where
     * all three are the same node.
     */
    function onDoubleClick(event) {
        if (!firstTap || !lastTap) return;
        if (now() - lastTap.at > RECORD_MS) return;
        if (firstTap.target !== event.target || lastTap.target !== event.target) return;
        reset();
        attempt(event.target);
    }

    function wire() {
        if (wired) return;
        // No full screen on this platform means no gesture: an iPhone would
        // otherwise carry a press that quietly does nothing.
        if (!FullscreenToggle.isSupported()) return;
        wired = true;
        // Bubble phase on purpose. Anything that claims a press for itself and
        // stops it propagating is a surface this gesture should not be reading.
        document.addEventListener('pointerdown', onDown, { passive: true });
        document.addEventListener('pointerup', onUp, { passive: true });
        document.addEventListener('pointercancel', reset, { passive: true });
        document.addEventListener('dblclick', onDoubleClick);
    }

    wire();
    document.addEventListener('DOMContentLoaded', wire);

    return { wire, isInert };
})();

export { FullscreenGesture };
